/**
 * Audit Trail — Immutable log of all PPM events.
 */
import React, { useState, useEffect } from 'react';

const API = process.env.REACT_APP_API_URL;

const EVENT_TYPE_LABELS = {
  recommendation_created: 'Recommendation Created',
  notification_sent: 'Notification Sent',
  decision_made: 'Owner Decision',
  approval_granted: 'Approval Granted',
  approval_rejected: 'Approval Rejected',
  policy_created: 'Policy Created',
  policy_updated: 'Policy Updated',
  policy_version_created: 'Version Created',
  policy_status_changed: 'Status Changed',
  field_value_updated: 'Field Updated',
};

const EVENT_COLORS = {
  recommendation_created: 'bg-blue-50 text-blue-700 border-blue-200',
  decision_made: 'bg-amber-50 text-amber-700 border-amber-200',
  approval_granted: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  approval_rejected: 'bg-red-50 text-red-600 border-red-200',
  policy_created: 'bg-amber-50 text-amber-700 border-amber-200',
  policy_updated: 'bg-slate-100 text-slate-600 border-slate-200',
  policy_version_created: 'bg-blue-50 text-blue-700 border-blue-200',
};

export default function AuditTrail() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/ppm/audit`, { credentials: 'include' });
        const data = await res.json();
        if (data.success) setEvents(data.events || []);
      } catch {}
      setLoading(false);
    })();
  }, []);

  return (
    <div className="p-8">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <h2 className="text-lg font-bold text-slate-800 tracking-tight">Audit Trail</h2>
          <p className="text-slate-400 text-xs mt-0.5">Immutable record of all policy management events</p>
        </div>

        {loading && <div className="text-slate-400 text-sm py-8">Loading audit trail...</div>}

        {!loading && events.length === 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-400 text-sm">
            No audit events recorded yet. Events are logged automatically as policies and recommendations are managed.
          </div>
        )}

        {!loading && events.length > 0 && (
          <div className="space-y-2">
            {events.map(event => {
              const colorClass = EVENT_COLORS[event.event_type] || 'bg-slate-100 text-slate-600 border-slate-200';
              return (
                <div key={event.id} className="bg-white rounded-xl border border-slate-200 px-5 py-3.5 flex items-center gap-4">
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border flex-shrink-0 ${colorClass}`}>
                    {EVENT_TYPE_LABELS[event.event_type] || event.event_type.replace(/_/g, ' ')}
                  </span>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-slate-700">
                      {event.actor_name || 'System'}
                    </span>
                    {event.entity_type && (
                      <span className="text-xs text-slate-400 ml-2">{event.entity_type.replace('ppm_', '').replace(/_/g, ' ')}</span>
                    )}
                  </div>
                  <span className="text-xs text-slate-400 flex-shrink-0">{new Date(event.created_at).toLocaleString()}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
